import { AdvancedSearchNodeType } from "../config/enums";
import { BitOperators, SelectOperators } from "../config/formEnums";
import { FilterNode, SearchColumn, SearchNode } from "./advancedSearch.types";
import { getAdvancedSearchEntityName, getAvailableFieldsFromSearchEntity } from "./searchFields";
import { MetadataSearchEntityNames, getSearchEntityConfig } from "./searchEntities";
import { SearchJoin } from "./searchEntities.types";

type Filters = { Key: string; Value: string }[];

/** The joined entities searching this one can reach. */
export function getJoinsFromSearchEntity(filters: Filters): SearchJoin[] {
  return getSearchEntityConfig(getAdvancedSearchEntityName(filters))?.joins ?? [];
}

export function filterJoinsByAllowedEntities<T extends { EntityName: string }>(
  joins: T[],
  allowedEntityTypes: Record<string, string> | null | undefined
) {
  if (!joins || !allowedEntityTypes) {
    return joins;
  }

  const allowed = Object.values(allowedEntityTypes);

  return joins.filter((join) => allowed.includes(join.EntityName));
}

/** Where a new record of the searched entity is made. */
export function getLinkFromSearchEntity(filters: Filters) {
  return getSearchEntityConfig(getAdvancedSearchEntityName(filters))?.recordRoute ?? "";
}

export function getDefaultOrderedByFromSearchEntity(entityName: string) {
  return getSearchEntityConfig(entityName)?.defaultOrderBy ?? "createdon";
}

/**
 * The entities whose metadata a search reads: the searched one and any joined entity
 * advanced search allows.
 */
export function getAttributeEntityNamesFromSearchEntity(
  filters: Filters,
  joins: { EntityName: string }[] | undefined
) {
  const entityName = getAdvancedSearchEntityName(filters);

  if (!entityName) {
    return [];
  }

  const entityNames = [entityName, ...(joins ?? []).map((join) => join.EntityName)];

  return entityNames.filter(
    (name, index) => entityNames.indexOf(name) === index && MetadataSearchEntityNames.includes(name)
  );
}

/** The rules a new search starts with. Anything unlisted starts with the status. */
export function getDefaultAdvancedNodesFromSearchEntity(entityName: string): SearchNode[] {
  const names = getSearchEntityConfig(entityName)?.defaultRules ?? ["statecode"];
  const available = getAvailableFieldsFromSearchEntity(entityName);

  return names
    .map((name) => available.find((field) => field.linkedEntityId === name || field.name === name))
    .filter((field): field is SearchColumn => Boolean(field))
    .map((field): FilterNode =>
      field.type === "bit"
        ? {
            ...field,
            alias: "",
            nodeType: AdvancedSearchNodeType.Filter,
            operator: BitOperators.Equals,
            value: field.defaultValue,
          }
        : {
            ...field,
            alias: "",
            name: field.linkedEntityId as string,
            nodeType: AdvancedSearchNodeType.Filter,
            operator: SelectOperators.Equals,
            value: "",
          }
    );
}
